import React from "react";
import type {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from "@dnd-kit/core";

/**
 * Touch has no hover, so the desktop "hover a section → Edit pill" affordance
 * can't survive on a phone. Mobile swaps it for tap-to-select: the tapped
 * section outlines itself and a single shared action bar (rendered once by
 * `WebsiteEditorMobileShell`) carries Edit / drag / dismiss.
 *
 * Only the mobile shell mounts the provider. On desktop the hook returns null
 * and every frame keeps its original hover chrome untouched.
 */
export type WebsiteEditorMobileSelection = {
  /** Frame identity, matching the id the section frame already uses. */
  id: string;
  /** Human label for the action bar ("Hero", "Site Header", …). */
  label: string;
  /** Opens the inspector for this section — the frame supplies its own. */
  open: () => void;
  moveUp?: () => void;
  moveDown?: () => void;
  /** True when this frame is in the page section order (arrows may still be disabled at the ends). */
  canReorder?: boolean;
};

/** dnd-kit handle props from the selected sortable section. */
export type WebsiteEditorMobileDragHandle = {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
};

type Ctx = {
  selection: WebsiteEditorMobileSelection | null;
  select: (next: WebsiteEditorMobileSelection | null) => void;
  clear: () => void;
  /**
   * Sortable frames publish their drag listeners here (ref, no React state) so
   * the floating bar can host the same grip without lifting listeners into
   * selection state (that would re-render loops).
   */
  setDragHandle: (
    id: string,
    handle: WebsiteEditorMobileDragHandle | null,
  ) => void;
  getDragHandle: (id: string) => WebsiteEditorMobileDragHandle | null;
};

const MobileSelectionCtx = React.createContext<Ctx | null>(null);

export function WebsiteEditorMobileSelectionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [selection, setSelection] =
    React.useState<WebsiteEditorMobileSelection | null>(null);
  const dragHandlesRef = React.useRef(
    new Map<string, WebsiteEditorMobileDragHandle>(),
  );

  const clear = React.useCallback(() => setSelection(null), []);

  const setDragHandle = React.useCallback(
    (id: string, handle: WebsiteEditorMobileDragHandle | null) => {
      if (handle) dragHandlesRef.current.set(id, handle);
      else dragHandlesRef.current.delete(id);
    },
    [],
  );

  const getDragHandle = React.useCallback(
    (id: string) => dragHandlesRef.current.get(id) ?? null,
    [],
  );

  const value = React.useMemo<Ctx>(
    () => ({
      selection,
      select: setSelection,
      clear,
      setDragHandle,
      getDragHandle,
    }),
    [selection, clear, setDragHandle, getDragHandle],
  );

  return (
    <MobileSelectionCtx.Provider value={value}>
      {children}
    </MobileSelectionCtx.Provider>
  );
}

/** Null on desktop — callers use that to keep hover behaviour. */
export function useWebsiteEditorMobileSelectionOptional(): Ctx | null {
  return React.useContext(MobileSelectionCtx);
}
